import { formatPropertyAddress, formatStreetAddress } from '../utils/propertyAddress';
import PropertyDetailDock from './PropertyDetailDock';
import './PropertyDetailDock.css';
import {PropertyListPicker} from './PropertyLists';
import { sendEmailRequest } from '../services/emailDeliveryService';
import { rememberPropertyPhoto } from '../services/propertyThumbnails';
import { getPropertyShareUrl } from '../utils/propertySharing';
import MapPlaceLayers from './MapPlaceLayers';
import PropertyMapPreview from './PropertyMapPreview';
import {mapPlaceCommands} from '../services/mapPlaces';
import React, { useEffect, useRef, useState, useImperativeHandle, useCallback } from 'react';
import { SITE_CONFIG } from '../config';
import { trackPropertyView } from '../services/analyticsService';
import { usePropertySave } from './PropertySaveProvider';
import L from 'leaflet';
import { addIllustratedBasemap } from '../services/illustratedBasemap';
import 'leaflet/dist/leaflet.css';
import './PropertyLeadModal.css';
import { mapCoordinates, zoomMap } from '../services/assistantNavigation';
import useDiscoveryDialog from '../hooks/useDiscoveryDialog';
import NearbyPlaces from './NearbyPlaces';
import ShowingRequest from './ShowingRequest';
import { rooms, roomPhotoIndexes } from '../services/assistantDiscovery';
import './AssistantDiscovery.css';
import GuestPropertyEmail from './GuestPropertyEmail';
import { assistantRequest } from '../services/assistantApi';
import { photoReference } from '../services/assistantPhotos';

const PropertyDetailModal = ({
  propertyDetail,
  propertyDetailImages,
  propertyMedia = [],
  imagesLoading,
  onClose,
  user,
  isAdmin,
  likedProperties,
  setLikedProperties,
  senderEmail,
  senderPassword,
  assistantEnabled = false,
  onAskProperty,
  assistantControlRef,
  onAssistantState,
  onAssistantResult,
  selectedNumber,
  onAssistantMessage,
  assistantBusy = false,
  voiceActive = false
}) => {
  const placeLayersRef=useRef(null);
  const [locationMapInstance,setLocationMapInstance]=useState(null);
  const nearbyControlRef = useRef(null), showingControlRef = useRef(null);
  const [photoTour,setPhotoTour] = useState(false);
  const photoTimerRef = useRef(null);
  const displayedPhotoRef = useRef(null);
  const pausePhotoTour = () => {clearTimeout(photoTimerRef.current);setPhotoTour(false);};
  const [roomFilter,setRoomFilter] = useState('');
  const [discoveryOpen,setDiscoveryOpen] = useState(false);
  const discoveryStatesRef=useRef({});
  const discoveryState = useCallback(state => { discoveryStatesRef.current={...discoveryStatesRef.current,...state};setDiscoveryOpen(Boolean(discoveryStatesRef.current.nearby_open || discoveryStatesRef.current.showing_open)); onAssistantState?.(state); },[onAssistantState]);
  const runNearby=params=>{if(['open_nearby','find_nearby','route_to'].includes(params.command)){setShowLocationMap(false);emailControlRef.current?.close();showingControlRef.current?.run({command:'cancel_showing'});setEnlargedImage(null);setShowAiStaging(false);}return nearbyControlRef.current?.run(params);};
  const runShowing=params=>{if(params.command!=='cancel_showing'){setShowLocationMap(false);emailControlRef.current?.close();nearbyControlRef.current?.run({command:'close_nearby'});setEnlargedImage(null);setShowAiStaging(false);}return showingControlRef.current?.run(params);};
  const availableRooms = Object.keys(rooms).filter(room => roomPhotoIndexes(propertyMedia,room).length);
  const availableRoomsKey = availableRooms.join(',');
  const roomIndexes = roomFilter ? roomPhotoIndexes(propertyMedia,roomFilter) : propertyDetailImages.map((_,i)=>i);
  const photoAt = index => {setPropertyAnswer('');setEnlargedImageIndex(index);setEnlargedImage(propertyDetailImages[index]);setStagingImage(null);setShowAiStaging(false);};
  const movePhoto = delta => {if(!roomIndexes.length)return;const position=roomIndexes.indexOf(enlargedImageIndex);photoAt(roomIndexes[(position+delta+roomIndexes.length)%roomIndexes.length]);};
  const chooseRoom = room => {const indexes=roomPhotoIndexes(propertyMedia,room);if(!indexes.length)return `No labeled ${rooms[room] || room} photos are available for this home. You can browse all photos.`;setRoomFilter(room);photoAt(indexes[0]);return `Showing ${rooms[room]} photo 1 of ${indexes.length}. Say next to browse this room, or close photos to return to the home.`;};
  const emailControlRef = useRef(null);
  const stagingBusyRef = useRef(false);
  const stagingGenerationRef = useRef(0);
  const activePhotoRef = useRef(null);
  const stagedUrlRef = useRef(null);
  const [stagingError, setStagingError] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [propertyQuestion, setPropertyQuestion] = useState('');
  const [propertyAnswer, setPropertyAnswer] = useState('');
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [enlargedImageIndex, setEnlargedImageIndex] = useState(0);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [showAiStaging, setShowAiStaging] = useState(false);
  const [stagingImage, setStagingImage] = useState(null);
  const [stagingLoading, setStagingLoading] = useState(false);
  const [showEmailSendModal, setShowEmailSendModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailCustomerName, setEmailCustomerName] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailGenerating, setEmailGenerating] = useState(false);
  const [emailMode, setEmailMode] = useState('email');
  const [socialCopy, setSocialCopy] = useState(null);
  const [showLocationMap, setShowLocationMap] = useState(false);
  const locationMapRef = useRef(null);
  const locationStartViewRef = useRef(null);
  const locationDialogRef = useDiscoveryDialog(showLocationMap, () => setShowLocationMap(false));
  const [locationZoom, setLocationZoom] = useState(null);
  const locationLeafletMapRef = useRef(null);
  const trackedListingKeyRef = useRef(null);
  const { favorites, toggleFavorite } = usePropertySave();
  useEffect(() => { setLikedProperties?.(favorites); }, [favorites, setLikedProperties]);

  const getValidVirtualTourUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      return ['http:', 'https:'].includes(parsedUrl.protocol) ? parsedUrl.href : '';
    } catch {
      return '';
    }
  };

  // Lock background scroll while the detail modal is open so the page behind the
  // (viewport-centered) modal doesn't scroll away under it.
  useEffect(() => {
    if (!propertyDetail) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [propertyDetail]);

  useEffect(() => {
    const listingKey = propertyDetail?.listing_key;
    if (!listingKey) {
      trackedListingKeyRef.current = null;
      return;
    }
    if (trackedListingKeyRef.current === listingKey) return;

    trackedListingKeyRef.current = listingKey;
    const pathname = window.location.pathname;
    let viewSurface = 'landing_page';
    if (pathname.startsWith('/listing/')) viewSurface = 'direct_listing';
    else if (pathname === '/properties/list') viewSurface = 'property_list';
    else if (pathname === '/search') {
      viewSurface = new URLSearchParams(window.location.search).get('searchLens') === 'true'
        ? 'search_lens'
        : 'search_results';
    } else if (pathname === '/chat') viewSurface = 'chat_results';

    trackPropertyView({ property: propertyDetail, surface: viewSurface });
  }, [propertyDetail]);

  const coordinates = mapCoordinates(propertyDetail);
  const mapLatitude = coordinates?.lat, mapLongitude = coordinates?.lon;
  const hasMapLocation = Boolean(coordinates);
  const closePhotos = () => {
    pausePhotoTour();
    stagingGenerationRef.current++; activePhotoRef.current = null;
    setRoomFilter(''); setEnlargedImage(null); setStagingImage(null); setShowAiStaging(false);
    return 'Back to the property details.';
  };
  const returnToDetails = () => {
    closePhotos(); setShowLocationMap(false);
    nearbyControlRef.current?.run({command:'close_nearby'});
    showingControlRef.current?.run({command:'cancel_showing'});
    emailControlRef.current?.close(); setShowEmailSendModal(false);
    return 'Back to the property details.';
  };
  const openLocationMap = view => {
    if (!hasMapLocation) return 'This home does not have usable map coordinates.';
    locationStartViewRef.current=view?.center && Number.isFinite(view.zoom)?view:null;
    returnToDetails(); setShowLocationMap(true);
    return 'The property map is open. Say zoom in, zoom out, or go back to the property details.';
  };
  useEffect(() => {
    onAssistantState?.({location_map_open:showLocationMap, location_map_ready:showLocationMap && locationZoom != null, location_map_zoom:showLocationMap ? locationZoom : null});
  }, [showLocationMap, locationZoom, onAssistantState]);

  useEffect(() => {
    if (!showLocationMap || !hasMapLocation || !locationMapRef.current) return undefined;
    locationLeafletMapRef.current?.remove();
    const view=locationStartViewRef.current;
    const map = L.map(locationMapRef.current, { scrollWheelZoom: true, minZoom: 1, maxZoom: 19 }).setView(view?[view.center.lat,view.center.lng]:[mapLatitude, mapLongitude], view?.zoom ?? 15);
    addIllustratedBasemap(map);
    const houseIcon = L.divIcon({
      html: '🏠', className: 'property-location-marker', iconSize: [30, 30], iconAnchor: [15, 15]
    });
    L.marker([mapLatitude, mapLongitude], { icon: houseIcon }).addTo(map);
    locationLeafletMapRef.current = map;setLocationMapInstance(map);
    setLocationZoom(map.getZoom());
    map.on('zoomend', () => setLocationZoom(map.getZoom()));
    const resize = () => map.invalidateSize();
    const resizeTimer = setTimeout(resize, 50);
    return () => {
      clearTimeout(resizeTimer);
      map.remove();
      locationLeafletMapRef.current = null;setLocationMapInstance(null);
    };
  }, [showLocationMap, hasMapLocation, mapLatitude, mapLongitude]);

  activePhotoRef.current = enlargedImage;
  displayedPhotoRef.current = stagingImage || enlargedImage;
  useEffect(() => {setPropertyAnswer('');}, [enlargedImage, stagingImage]);
  useEffect(() => {
    if (!photoTour) return;
    if (!enlargedImage || showAiStaging || stagingImage || shareOpen || discoveryOpen || document.hidden) {setPhotoTour(false);return;}
    const position=roomIndexes.indexOf(enlargedImageIndex);
    if(position<0 || position>=roomIndexes.length-1) {setPhotoTour(false);return;}
    photoTimerRef.current=setTimeout(()=>photoAt(roomIndexes[position+1]),5000);
    const hide=()=>{if(document.hidden)pausePhotoTour();};
    document.addEventListener('visibilitychange',hide);
    return ()=>{clearTimeout(photoTimerRef.current);document.removeEventListener('visibilitychange',hide);};
  // The timer advances within the current room, and never loops or calls a model.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoTour,enlargedImage,enlargedImageIndex,roomFilter,propertyDetailImages.length,showAiStaging,stagingImage,shareOpen,discoveryOpen]);
  useEffect(() => {
    stagingGenerationRef.current += 1;
    pausePhotoTour();
    setEnlargedImage(null); setEnlargedImageIndex(0); setStagingImage(null);
    setShowLocationMap(false); setLocationZoom(null);
    setRoomFilter(''); setDiscoveryOpen(false); setShowAiStaging(false); setStagingError(''); setPropertyAnswer(''); setPropertyQuestion('');
    return () => {
      stagingGenerationRef.current += 1;
      if (stagedUrlRef.current) URL.revokeObjectURL(stagedUrlRef.current);
    };
  }, [propertyDetail?.listing_key]);
  useEffect(() => {
    onAssistantState?.({ gallery_open: Boolean(enlargedImage), photo_number: enlargedImage ? enlargedImageIndex + 1 : null, photo_count: propertyDetailImages.length, photo_ref: enlargedImage ? photoReference(enlargedImage) : null, photo_staged: Boolean(stagingImage), photo_tour: photoTour, staging_open: showAiStaging, room_filter: roomFilter, available_rooms: availableRoomsKey ? availableRoomsKey.split(',') : [] });
  }, [enlargedImage, enlargedImageIndex, propertyDetailImages.length, stagingImage, photoTour, showAiStaging, roomFilter, availableRoomsKey, onAssistantState]);
  const emailState = useCallback(state => { setShareOpen(state.email_open); onAssistantState?.({...state,email_open:state.email_open || showEmailSendModal}); }, [onAssistantState, showEmailSendModal]);
  useEffect(() => {onAssistantState?.({email_open:shareOpen || showEmailSendModal});}, [shareOpen, showEmailSendModal, onAssistantState]);
  useImperativeHandle(assistantControlRef, () => ({
    showPhotoAnswer: (answer, reference) => {if(enlargedImage && !stagingImage && photoReference(enlargedImage)===reference)setPropertyAnswer(answer);},
    run: async (params) => {
      const { command, number, room_type, email, room } = params;
      if (!propertyDetail) return 'Open a property first.';
      if (command !== 'start_photo_tour') pausePhotoTour();
      if (command === 'stop_photo_tour') return 'Photo scrolling is paused.';
      if (command === 'start_photo_tour') {
        if (!propertyDetailImages.length) return 'No photos are available yet. Open the photos once they finish loading.';
        if (!enlargedImage) {returnToDetails();setRoomFilter('');photoAt(0);}
        else {setStagingImage(null);setShowAiStaging(false);if(roomIndexes.indexOf(enlargedImageIndex)===roomIndexes.length-1)photoAt(roomIndexes[0]);}
        setPhotoTour(true);
        return 'Scrolling through the photos every five seconds. Say pause to stop on a photo.';
      }
      if (command === 'return_to_property') return returnToDetails();
      if (mapPlaceCommands.includes(command)) return showLocationMap ? placeLayersRef.current?.run(params,params.isCurrent) || 'The map is loading.' : discoveryStatesRef.current.nearby_open ? runNearby(params) : 'Open the map first, then ask for nearby places.';
      if (command === 'open_map') return openLocationMap();
      if (command === 'close_map') {
        if (showLocationMap) {setShowLocationMap(false); return 'Back to the property details.';}
        if (discoveryStatesRef.current.nearby_open) return runNearby({command:'close_nearby'});
        return 'There is no map open.';
      }
      if (['zoom_map_in','zoom_map_out'].includes(command)) {
        if (showLocationMap) return zoomMap(locationLeafletMapRef.current, command === 'zoom_map_in' ? 1 : -1);
        if (discoveryStatesRef.current.nearby_open) return runNearby(params);
        return 'Open the map first, then ask me to zoom in or out.';
      }
      if (command === 'close_staging') {setShowAiStaging(false); return 'Back to the photo.';}
      if (command === 'go_back') {
        if (showLocationMap) {setShowLocationMap(false); return 'Back to the property details.';}
        if (shareOpen) return emailControlRef.current?.close();
        if (showEmailSendModal) {setShowEmailSendModal(false); return 'Back to the property details.';}
        if (discoveryStatesRef.current.showing_open) return runShowing({command:'cancel_showing'});
        if (discoveryStatesRef.current.nearby_open) return runNearby({command:'close_nearby'});
        if (showAiStaging) {setShowAiStaging(false); return 'Back to the photo.';}
        if (enlargedImage) return closePhotos();
        onClose(); return 'Back to the property results.';
      }
      if (['open_nearby','find_nearby','route_to','close_nearby'].includes(command)) return runNearby(params);
      if (['open_showing','update_showing','submit_showing','cancel_showing'].includes(command)) return runShowing(params);
      if (command === 'show_room') {returnToDetails(); return chooseRoom(room);}
      if (command === 'next_room_photo') {if(room && room !== roomFilter)return chooseRoom(room);if(!roomFilter)return 'Choose a room first.';movePhoto(1);return 'Showing the next labeled room photo.';}
      if (command === 'prepare_email') return emailControlRef.current?.prepare(email, params.name) || 'Property email is unavailable.';
      if (command === 'send_email') return emailControlRef.current?.send() || 'Prepare an email first.';
      if (command === 'cancel_email') {setShowEmailSendModal(false); return emailControlRef.current?.close() || 'Back to the property details.';}
      if (command === 'close_photos') return closePhotos();
      if (command === 'show_original') { setStagingImage(null); return 'Showing the original photo.'; }
      if (!propertyDetailImages.length) return imagesLoading ? 'The photos are still loading. Please try again in a moment.' : 'No photos are available for this property.';
      if (command === 'open_photos') { returnToDetails(); setRoomFilter('');
        setEnlargedImageIndex(0); setEnlargedImage(propertyDetailImages[0]); setStagingImage(null);
        return `Photo one of ${propertyDetailImages.length} is open. Say next or previous to browse.`;
      }
      if (!enlargedImage) return 'Open the photos first.';
      if (command === 'open_staging') { setShowAiStaging(true); return 'Choose Bedroom, Office, Landscape, or Room on the screen, or say your choice.'; }
      if (command === 'stage_room') {
        setShowAiStaging(true);
        if (!['bedroom', 'office', 'landscape', 'room'].includes(room_type)) return 'Choose Bedroom, Office, Landscape, or Room.';
        return handleAiStaging(room_type);
      }
      if (roomFilter && ['next_photo','previous_photo'].includes(command)) {movePhoto(command === 'next_photo'?1:-1);return `Showing the ${command === 'next_photo' ? 'next' : 'previous'} ${rooms[roomFilter]} photo.`;}
      if (command === 'show_photo') setRoomFilter('');
      const next = command === 'show_photo' ? number - 1 : command === 'next_photo' ? enlargedImageIndex + 1 : command === 'previous_photo' ? enlargedImageIndex - 1 : NaN;
      if (!Number.isInteger(next)) return 'That photo command is unavailable.';
      if (next < 0 || next >= propertyDetailImages.length) return `Choose a photo from one to ${propertyDetailImages.length}.`;
      photoAt(next);
      return `Showing photo ${next + 1} of ${propertyDetailImages.length}.`;
    }
  }));

  if (!propertyDetail) return null;

  const virtualTourUrl = getValidVirtualTourUrl(propertyDetail.virtual_tour_url);
  const propertyAddress = formatPropertyAddress(propertyDetail);
  const totalBathrooms = propertyDetail.bathrooms_total_integer
    ?? propertyDetail.bathrooms_total
    ?? ((propertyDetail.bathrooms_full || 0) + ((propertyDetail.bathrooms_half || 0) * 0.5));
  const numericLivingArea = Number(String(propertyDetail.living_area || '').replace(/,/g, ''));
  const formattedLivingArea = Number.isFinite(numericLivingArea)
    ? numericLivingArea.toLocaleString()
    : propertyDetail.living_area;
  const isPropertySaved = likedProperties.has(propertyDetail.listing_key);
  const handleLike = () => toggleFavorite(propertyDetail, user);

  const handleAiStaging = async (type) => {
    if (stagingBusyRef.current) return 'A staging request is already running.';
    stagingBusyRef.current = true; setStagingLoading(true); setStagingError('');
    const sourcePhoto = enlargedImage;
    const version = stagingGenerationRef.current;
    try {
      let blob;
      if (assistantEnabled) {
        blob = await assistantRequest('/stage', { listing_key: propertyDetail.listing_key, photo_number: enlargedImageIndex + 1, room_type: type, request_id: window.crypto?.randomUUID?.() || `stage-${Date.now()}-${Math.random().toString(36).slice(2)}` }, { blob: true });
      } else {
        const promptResponse = await fetch(`https://tophamtechbeb.com/api/stage/v1/${type}-prompts/random`, { headers: { 'X-API-Key': 'treas-api-key-2025' } });
        if (!promptResponse.ok) throw new Error('Failed to get staging prompt');
        const promptData = await promptResponse.json();
        const formData = new FormData(); formData.append('image_url', sourcePhoto); formData.append('design_prompt', promptData.prompt);
        const response = await fetch('https://tophamtechbeb.com/api/stage/v1/stage-room', { method: 'POST', headers: { 'X-API-Key': 'treas-api-key-2025' }, body: formData });
        if (!response.ok) throw new Error('Failed to stage room');
        blob = await response.blob();
      }
      if (version !== stagingGenerationRef.current || activePhotoRef.current !== sourcePhoto) return 'Staging finished for an earlier photo. Your current photo has not changed.';
      if (stagedUrlRef.current) URL.revokeObjectURL(stagedUrlRef.current);
      stagedUrlRef.current = URL.createObjectURL(blob);
      setStagingImage(stagedUrlRef.current); setShowAiStaging(false);
      return 'The virtually staged image is ready. You can say show the original to compare.';
    } catch (e) { if (version === stagingGenerationRef.current) setStagingError(e.message); return e.message; }
    finally { stagingBusyRef.current = false; setStagingLoading(false); }
  };

  return (
    <>
      <div className="expanded-map-modal property-detail-overlay" onClick={onClose}>
        <div data-assistant-surface="property" data-listing-key={propertyDetail.listing_key} className="property-detail-modal property-detail-modal--premium" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Property details for ${propertyAddress}`}>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "RealEstateProperty",
              "name": formatPropertyAddress(propertyDetail),
              "address": {
                "@type": "PostalAddress",
                "streetAddress": formatStreetAddress(propertyDetail),
                "addressLocality": propertyDetail.location?.city,
                "addressRegion": propertyDetail.location?.state,
                "postalCode": propertyDetail.location?.postal_code
              },
              "price": propertyDetail.current_price,
              "priceCurrency": "USD",
              "numberOfRooms": propertyDetail.bedrooms_total,
              "numberOfBathroomsTotal": propertyDetail.bathrooms_full + (propertyDetail.bathrooms_half || 0) * 0.5,
              "floorSize": { "@type": "QuantitativeValue", "value": propertyDetail.living_area, "unitCode": "SQF" },
              "propertyType": propertyDetail.property_type,
              "yearBuilt": propertyDetail.year_built,
              "image": propertyDetailImages,
              "description": propertyDetail.features?.public_remarks,
              "url": `${window.location.origin}/listing/${propertyDetail.listing_key}`
            })}
          </script>
          <div className="property-detail-actions" aria-label="Property actions">
            <button
              className="link-button"
              onClick={() => {
                const url = getPropertyShareUrl(propertyDetail, SITE_CONFIG);
                navigator.clipboard.writeText(url).then(() => {
                  setShowCopySuccess(true);
                  setTimeout(() => setShowCopySuccess(false), 3000);
                }).catch(() => alert('Failed to copy link'));
              }}
              aria-label="Copy property link to clipboard"
            >
              <span aria-hidden="true">↗</span><small>Share</small>
            </button>
            <button
              className={`heart-button ${isPropertySaved ? 'liked' : ''}`}
              onClick={handleLike}
              aria-label={isPropertySaved ? "Remove property from favorites" : "Save property to favorites"}
            >
              <span aria-hidden="true">{isPropertySaved ? '♥' : '♡'}</span><small>{isPropertySaved ? 'Saved' : 'Save'}</small>
            </button>
            {isPropertySaved && <PropertyListPicker listingKey={propertyDetail.listing_key} />}
            {assistantEnabled && !isAdmin && <button type="button" className="email-send-button" onClick={() => emailControlRef.current?.prepare('')} aria-label="Email this property"><span aria-hidden="true">✉</span><small>Email</small></button>}
            {isAdmin && (
              <button
                className="email-send-button"
                onClick={() => {
                  setEmailSubject(`Property Listing - ${formatPropertyAddress(propertyDetail)}`);
                  setEmailRecipient('');
                  setEmailCustomerName('');
                  setEmailPreviewHtml('');
                  setShowEmailSendModal(true);
                }}
                aria-label="Send property email"
              >
                <span aria-hidden="true">✉</span><small>Email</small>
              </button>
            )}
            {selectedNumber && <span className="assistant-property-number" aria-label={`Property ${selectedNumber}`}>{selectedNumber}</span>}
            <button className="close-expanded-map" onClick={onClose} aria-label="Close property details"><span aria-hidden="true">×</span></button>
          </div>
          {showEmailSendModal && (
            <div data-assistant-surface="email" className="email-send-modal" onClick={(e) => e.stopPropagation()}>
              <div className="email-send-modal-content">
                <button className="email-send-modal-close" onClick={() => { setShowEmailSendModal(false); setSocialCopy(null); }}>×</button>
                <div className="email-send-tabs">
                  <button className={emailMode === 'email' ? 'active' : ''} onClick={() => { setEmailMode('email'); setSocialCopy(null); }}>Email</button>
                  <button className={emailMode === 'social' ? 'active' : ''} onClick={() => { setEmailMode('social'); setEmailPreviewHtml(''); }}>Social</button>
                </div>
                {emailMode === 'email' ? (
                  <>
                    {emailPreviewHtml === 'sent' ? (
                      <div style={{textAlign: 'center', padding: '30px 10px'}}>
                        <div style={{fontSize: '40px', marginBottom: '10px'}}>✅</div>
                        <p style={{color: '#28a745', fontWeight: 600, margin: 0}}>Email sent successfully!</p>
                      </div>
                    ) : !emailPreviewHtml ? (
                      <div className="email-send-form">
                        <input type="email" placeholder="Recipient email *" value={emailRecipient} onChange={(e) => setEmailRecipient(e.target.value)} />
                        <input type="text" placeholder="Customer name (optional)" value={emailCustomerName} onChange={(e) => setEmailCustomerName(e.target.value)} />
                        <input type="text" placeholder="Subject *" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                        <button
                          disabled={!emailRecipient.trim() || !emailSubject.trim() || emailGenerating}
                          onClick={async () => {
                            setEmailGenerating(true);
                            try {
                              const res = await fetch('https://tophamtechbe.com/api/treas/comm/property-search-preview?format=html', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'X-API-Key': 'treas-api-key-2025' },
                                body: JSON.stringify({
                                  customerName: emailCustomerName || undefined,
                                  agentName: SITE_CONFIG.AGENT.NAME,
                                  siteId: SITE_CONFIG.SITE_ID,
                                  listingBaseUrl: `https://www.${SITE_CONFIG.BASE_URL}`,
                                  searchFilters: { listingKey: propertyDetail.listing_key }
                                })
                              });
                              const data = await res.json();
                              if (data.html) setEmailPreviewHtml(data.html);
                              else alert(data.message || 'No preview generated');
                            } catch (err) { alert('Error generating preview'); }
                            setEmailGenerating(false);
                          }}
                        >
                          {emailGenerating ? 'Generating...' : 'Preview Email'}
                        </button>
                      </div>
                    ) : (
                      <div className="email-send-preview">
                        <iframe title="Email Preview" srcDoc={emailPreviewHtml} style={{width: '100%', height: '300px', border: '1px solid #ddd', borderRadius: '4px'}} />
                        <div className="email-send-actions">
                          <button onClick={() => setEmailPreviewHtml('')}>← Back</button>
                          <button
                            disabled={emailSending}
                            onClick={async () => {
                              setEmailSending(true);
                              try {
                                const formData = new FormData();
                                formData.append('emailaddress', emailRecipient);
                                formData.append('title', emailSubject);
                                formData.append('body', emailPreviewHtml);
                                if (senderEmail) formData.append('email', senderEmail);
                                if (senderPassword) formData.append('emailPassword', senderPassword);
                                const res = await sendEmailRequest('https://tophamtechbe.com/api/treas/comm/send-email', {
                                  method: 'POST',
                                  headers: { 'X-API-Key': 'treas-api-key-2025' },
                                  body: formData
                                });
                                if (res.ok) {
                                  setEmailPreviewHtml('sent');
                                  setTimeout(() => setShowEmailSendModal(false), 2000);
                                } else alert('Failed to send email');
                              } catch (err) { alert('Error sending email'); }
                              setEmailSending(false);
                            }}
                          >
                            {emailSending ? 'Sending...' : 'Send Email'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="email-send-form">
                    {!socialCopy ? (
                      <button
                        disabled={emailGenerating}
                        onClick={async () => {
                          setEmailGenerating(true);
                          try {
                            const res = await fetch('https://tophamtechbe.com/api/treas/comm/property-search-preview?format=social', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'X-API-Key': 'treas-api-key-2025' },
                              body: JSON.stringify({
                                agentName: SITE_CONFIG.AGENT.NAME,
                                siteId: SITE_CONFIG.SITE_ID,
                                listingBaseUrl: `https://www.${SITE_CONFIG.BASE_URL}`,
                                searchFilters: { listingKey: propertyDetail.listing_key }
                              })
                            });
                            const data = await res.json();
                            if (data.text) setSocialCopy(data);
                            else alert(data.message || 'No content generated');
                          } catch (err) { alert('Error generating social copy'); }
                          setEmailGenerating(false);
                        }}
                      >
                        {emailGenerating ? 'Generating...' : 'Generate Social Post'}
                      </button>
                    ) : (
                      <div className="social-copy-result">
                        <textarea readOnly value={socialCopy.text} style={{width: '100%', height: '150px', resize: 'none', borderRadius: '6px', border: '1px solid #ddd', padding: '10px', fontSize: '13px', boxSizing: 'border-box'}} />
                        {socialCopy.images?.length > 0 && (
                          <div style={{display: 'flex', gap: '6px', marginTop: '8px', overflowX: 'auto'}}>
                            {socialCopy.images.map((img, i) => (
                              <div key={i} style={{position: 'relative', flexShrink: 0}}>
                                <img src={img} alt="" style={{height: '60px', borderRadius: '4px'}} />
                                <button onClick={() => window.open(img, '_blank')} style={{position: 'absolute', bottom: '2px', right: '2px', background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: '4px', padding: '2px 5px', fontSize: '10px', border: 'none', cursor: 'pointer'}}>⬇</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <button style={{marginTop: '10px'}} onClick={() => { navigator.clipboard.writeText(socialCopy.text); }}>📋 Copy Text</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="property-detail-content">
            <div className="property-header">
              <div className={`property-overview-grid${hasMapLocation ? '' : ' property-overview-grid--no-map'}`}>
              <div className="property-overview-summary">
              {!propertyDetail.isPrivate && (propertyDetail.list_agent_full_name || propertyDetail.list_office_name) && (
                <div className="property-listing-attribution">
                  {propertyDetail.list_agent_full_name && (
                    <div>Listing Agent: {propertyDetail.list_agent_full_name}{propertyDetail.list_agent_mls_id ? ` (${propertyDetail.list_agent_mls_id})` : ''}</div>
                  )}
                  {propertyDetail.list_office_name && (
                    <div>Office: {propertyDetail.list_office_name}</div>
                  )}
                </div>
              )}
              <p className="property-detail-eyebrow">{propertyDetail.property_sub_type || propertyDetail.property_type || 'Local residence'}</p>
              <h2>${propertyDetail.current_price?.toLocaleString()}</h2>
              <p className="property-address">{propertyAddress}</p>
              <div className="property-key-facts" aria-label="Property overview">
                {propertyDetail.bedrooms_total != null && <span><strong>{propertyDetail.bedrooms_total}</strong><small>Beds</small></span>}
                {totalBathrooms > 0 && <span><strong>{totalBathrooms}</strong><small>Baths</small></span>}
                {propertyDetail.living_area && <span><strong>{formattedLivingArea}</strong><small>Sq ft</small></span>}
                {propertyDetail.year_built && <span><strong>{propertyDetail.year_built}</strong><small>Built</small></span>}
              </div>
              </div>
              {hasMapLocation && <PropertyMapPreview property={propertyDetail} onExpand={openLocationMap} />}
              </div>
              <div className="property-images">
                {imagesLoading ? (
                  <div className="property-images-status">
                    <div style={{display: 'inline-block', marginRight: '8px', animation: 'spin 1s linear infinite', fontSize: '16px'}}>⟳</div>
                    Checking for images...
                  </div>
                ) : propertyDetailImages.length > 0 ? (
                  propertyDetailImages.map((imageUrl, index) => (
                    <div key={index} className="property-detail-image" onClick={() => { setEnlargedImage(imageUrl); setEnlargedImageIndex(index); }}>
                      <img src={imageUrl} alt={`Property ${index + 1}`} onLoad={() => rememberPropertyPhoto(propertyDetail?.listing_key, imageUrl)} />
                    </div>
                  ))
                ) : (
                  <div className="property-images-status property-images-empty">
                    No images available for this property
                  </div>
                )}
              </div>
              {virtualTourUrl && (
                <a
                  className="virtual-tour-cta"
                  href={virtualTourUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="virtual-tour-cta-title">View 3D Virtual Tour</span>
                  <span className="virtual-tour-cta-subtitle">Walk through this home in 360°</span>
                </a>
              )}
              {propertyDetail.features?.public_remarks && (
                <div className="property-description">
                  <h3>Property Description</h3>
                  <p>{propertyDetail.features.public_remarks}</p>
                </div>
              )}
            </div>
            <div className="property-columns">
              <div className="property-column">
                <h3>Property Details</h3>
                <p><strong>Property Type:</strong> {propertyDetail.property_type}</p>
                {propertyDetail.property_sub_type && <p><strong>Property Sub Type:</strong> {propertyDetail.property_sub_type}</p>}
                <p><strong>Listing Key:</strong> {propertyDetail.listing_key}</p>
                <p><strong>Current Price:</strong> ${propertyDetail.current_price?.toLocaleString()}</p>
                <p><strong>List Price:</strong> ${propertyDetail.list_price?.toLocaleString()}</p>
                {propertyDetail.original_list_price && <p><strong>Original List Price:</strong> ${propertyDetail.original_list_price?.toLocaleString()}</p>}
                <p><strong>Living Area:</strong> {propertyDetail.living_area} sq ft</p>
                <p><strong>Building Area:</strong> {propertyDetail.building_area_total} sq ft</p>
                <p><strong>Year Built:</strong> {propertyDetail.year_built}</p>
                {propertyDetail.stories && <p><strong>Stories:</strong> {propertyDetail.stories}</p>}
                {propertyDetail.listing_contract_date && <p><strong>Days on Market:</strong> {Math.floor((Date.now() - new Date(propertyDetail.listing_contract_date)) / 86400000) || 'First Day on Market!'}</p>}
                {propertyDetail.listing_contract_date && <p><strong>Listed Date:</strong> {new Date(propertyDetail.listing_contract_date).toLocaleDateString()}</p>}
                {propertyDetail.prop_sync_last_synced_at && <p><strong>Last Updated:</strong> {new Date(propertyDetail.prop_sync_last_synced_at).toLocaleString()}</p>}
                {propertyDetail.land && (
                  <div>
                    <h3>Land Details</h3>
                    {propertyDetail.land.lot_size_acres && <p><strong>Lot Size:</strong> {propertyDetail.land.lot_size_acres} acres ({propertyDetail.land.lot_size_square_feet?.toLocaleString()} sq ft)</p>}
                    {propertyDetail.land.lot_features && <p><strong>Lot Features:</strong> {propertyDetail.land.lot_features}</p>}
                    {propertyDetail.land.zoning && <p><strong>Zoning:</strong> {propertyDetail.land.zoning}</p>}
                  </div>
                )}
                <h3>Rooms & Bathrooms</h3>
                {propertyDetail.bedrooms_total != null && <p><strong>Bedrooms:</strong> {propertyDetail.bedrooms_total}</p>}
                {propertyDetail.bathrooms_full != null && <p><strong>Full Bathrooms:</strong> {propertyDetail.bathrooms_full}</p>}
                {propertyDetail.bathrooms_half != null && <p><strong>Half Bathrooms:</strong> {propertyDetail.bathrooms_half}</p>}
                {propertyDetail.fireplaces_total != null && <p><strong>Fireplaces:</strong> {propertyDetail.fireplaces_total}</p>}
                {propertyDetail.schools && (
                  <div>
                    <h3>Schools</h3>
                    <p><strong>Elementary:</strong> {propertyDetail.schools.elementary_school}</p>
                    <p><strong>Middle:</strong> {propertyDetail.schools.middle_school}</p>
                    <p><strong>High:</strong> {propertyDetail.schools.high_school}</p>
                  </div>
                )}
              </div>
              <div className="property-column">
                <h3>Features & Amenities</h3>
                {propertyDetail.features?.garage_spaces != null && <p><strong>Garage:</strong> {propertyDetail.features.garage_spaces} spaces</p>}
                {propertyDetail.features?.heating && <p><strong>Heating:</strong> {propertyDetail.features.heating}</p>}
                {propertyDetail.features?.cooling && <p><strong>Cooling:</strong> {propertyDetail.features.cooling}</p>}
                {propertyDetail.features?.water_source && <p><strong>Water Source:</strong> {propertyDetail.features.water_source}</p>}
                {propertyDetail.foundation_details && <p><strong>Foundation:</strong> {propertyDetail.foundation_details}</p>}
                {propertyDetail.roof && <p><strong>Roof:</strong> {propertyDetail.roof}</p>}
                {propertyDetail.sewer && <p><strong>Sewer:</strong> {propertyDetail.sewer}</p>}
                {propertyDetail.features?.appliances && (
                  <div>
                    <h3>Appliances</h3>
                    <p className="feature-list">{propertyDetail.features.appliances.split(',').join(', ')}</p>
                  </div>
                )}
                {propertyDetail.features?.interior_features && (
                  <div>
                    <h3>Interior Features</h3>
                    <p className="feature-list">{propertyDetail.features.interior_features.split(',').join(', ')}</p>
                  </div>
                )}
                {propertyDetail.features?.exterior_features && (
                  <div>
                    <h3>Exterior Features</h3>
                    <p className="feature-list">{propertyDetail.features.exterior_features.split(',').join(', ')}</p>
                  </div>
                )}
                {propertyDetail.features?.fencing && (
                  <div>
                    <h3>Fencing</h3>
                    <p>{propertyDetail.features.fencing}</p>
                  </div>
                )}
                {propertyDetail.association?.association_name && (
                  <div>
                    <h3>HOA Information</h3>
                    <p><strong>Association:</strong> {propertyDetail.association.association_name}</p>
                    {propertyDetail.association.association_fee && <p><strong>HOA Fee:</strong> ${propertyDetail.association.association_fee} {propertyDetail.association.fee_frequency}</p>}
                  </div>
                )}
                {propertyDetail.features?.lots_features && (
                  <div>
                    <h3>Lot Features</h3>
                    <p className="feature-list">{propertyDetail.features.lots_features.split(',').join(', ')}</p>
                  </div>
                )}
                {propertyDetail.features?.patio_and_porch_features && (
                  <div>
                    <h3>Patio & Porch</h3>
                    <p className="feature-list">{propertyDetail.features.patio_and_porch_features.split(',').join(', ')}</p>
                  </div>
                )}
              </div>
            </div>
            {!propertyDetail.isPrivate && SITE_CONFIG.MLS && (
              <div className="property-mls-footer">
                <img src={SITE_CONFIG.MLS.LOGO_URL} alt={SITE_CONFIG.MLS.SHORT_NAME} style={{height: '32px', objectFit: 'contain'}} />
                <div>
                  <span>{SITE_CONFIG.MLS.DISCLAIMER}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {assistantEnabled && <PropertyDetailDock key={propertyDetail.listing_key} reply={propertyAnswer} busy={assistantBusy}>
      {assistantEnabled && onAssistantMessage && !shareOpen && !discoveryOpen && !showEmailSendModal && !showAiStaging && <form className={`assistant-detail-question ${voiceActive ? 'assistant-detail-question-voice' : ''}`} onSubmit={async e => {
        e.preventDefault(); if (!propertyQuestion.trim() || assistantBusy) return;
        const question = propertyQuestion; const version = stagingGenerationRef.current; const photo = displayedPhotoRef.current; pausePhotoTour(); setPropertyQuestion('');
        const answer = await onAssistantMessage(question);
        if (version === stagingGenerationRef.current && photo === displayedPhotoRef.current) setPropertyAnswer(answer || '');
      }}>
        {propertyAnswer && <p role="status">{propertyAnswer}</p>}
        <label className="sr-only" htmlFor="assistant-home-question">Ask about this home</label>
        <input id="assistant-home-question" value={propertyQuestion} onChange={e => setPropertyQuestion(e.target.value)} placeholder={enlargedImage ? 'Ask about this photo, or say “next photo”…' : 'Ask about this home or type a command…'} />
        <button type="submit" disabled={assistantBusy || !propertyQuestion.trim()}>{assistantBusy ? 'Thinking…' : 'Ask'}</button>
      </form>}
      {assistantEnabled && <div className={`discovery-detail-actions ${!onAssistantMessage ? 'discovery-detail-actions-standalone' : ''}`}>{!onAssistantMessage && onAskProperty && <button onClick={onAskProperty}>Ask about this home</button>}<button onClick={()=>runNearby({command:'open_nearby'})}>Around this home</button><button onClick={()=>runShowing({command:'open_showing'})}>Request a showing</button></div>}
      </PropertyDetailDock>}
      {assistantEnabled && <MapPlaceLayers map={showLocationMap ? locationMapInstance : null} listingKey={propertyDetail.listing_key} controlRef={placeLayersRef} onState={onAssistantState} onMessage={onAssistantMessage} />}
      {assistantEnabled && <NearbyPlaces home={propertyDetail} listingKey={propertyDetail.listing_key} controlRef={nearbyControlRef} onState={discoveryState} onResult={onAssistantResult} />}
      {assistantEnabled && <ShowingRequest listingKey={propertyDetail.listing_key} controlRef={showingControlRef} onState={discoveryState} onResult={onAssistantResult} contact={{name:user?.name || user?.username || '',email:user?.email || '',phone:user?.phoneNumber || ''}} />}
      {assistantEnabled && <GuestPropertyEmail listingKey={propertyDetail.listing_key} controlRef={emailControlRef} onState={emailState} onResult={onAssistantResult} />}
      {enlargedImage && (
        <div data-assistant-surface="photos" className="gallery-overlay" onClick={closePhotos}>
          <div className={`gallery-container ${assistantEnabled ? "assistant-photo-gallery" : ""}`} onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => { e.currentTarget.dataset.touchStartX = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              const startX = parseFloat(e.currentTarget.dataset.touchStartX);
              const endX = e.changedTouches[0].clientX;
              const diff = startX - endX;
              if (Math.abs(diff) > 50) {pausePhotoTour();movePhoto(diff > 0 ? 1 : -1);}
            }}
          >
            <div className="gallery-top-bar">
              <span className="gallery-counter">{enlargedImageIndex + 1} / {propertyDetailImages.length}</span>
              <div className="gallery-top-actions">
                <button className={`gallery-btn gallery-heart ${likedProperties.has(propertyDetail?.listing_key) ? 'liked' : ''}`}
                  onClick={handleLike}
                >{likedProperties.has(propertyDetail?.listing_key) ? '💖' : '🤍'}</button>
                <button aria-label="Stage this photo" title="Stage this photo" className="gallery-btn" onClick={() => setShowAiStaging(true)}>🤖</button>
                <button aria-label="Close photos" className="gallery-btn" onClick={closePhotos}>✕</button>
              </div>
            </div>
            {assistantEnabled && <div className="room-filter-controls"><label>Room <select aria-label="Filter photos by room" value={roomFilter} onChange={e=>{pausePhotoTour();if(e.target.value)chooseRoom(e.target.value);else setRoomFilter('');}}><option value="">All photos</option>{availableRooms.map(room=><option key={room} value={room}>{rooms[room]}</option>)}</select></label>{roomFilter && <span>{roomIndexes.indexOf(enlargedImageIndex)+1} of {roomIndexes.length} labeled photos</span>}</div>}
            {assistantEnabled && <div className="assistant-photo-controls">
              <button type="button" disabled={assistantBusy || propertyDetailImages.length<2} onClick={async()=>{const result=await assistantControlRef?.current?.run({command:photoTour?'stop_photo_tour':'start_photo_tour'});onAssistantResult?.(result);}}>{photoTour?'Pause photos':'Play photos'}</button>
              {onAssistantMessage && <button type="button" disabled={assistantBusy || Boolean(stagingImage)} onClick={async()=>{
                pausePhotoTour();const photo=displayedPhotoRef.current;const version=stagingGenerationRef.current;
                const answer=await onAssistantMessage('Describe this photo');
                if(version===stagingGenerationRef.current && photo===displayedPhotoRef.current)setPropertyAnswer(answer || '');
              }}>{assistantBusy?'Looking…':'Describe photo'}</button>}
              <span>{photoTour?'Every 5 seconds · Say “pause”':stagingImage?'Choose Original to describe the listing photo':'Say “next photo” or “what do you see?”'}</span>
            </div>}
            <img src={stagingImage || enlargedImage} alt="Property" className="gallery-image" />
            {stagingImage && <span className="assistant-staging-label">Virtually staged · Illustrative only</span>}
            {stagingImage && (
              <button className="reset-staging-button" onClick={() => setStagingImage(null)}>Original</button>
            )}
            {propertyDetailImages.length > 1 && (
              <>
                <button className="gallery-arrow gallery-prev" aria-label="Previous photo" onClick={()=>{pausePhotoTour();movePhoto(-1);}}>‹</button>
                <button className="gallery-arrow gallery-next" aria-label="Next photo" onClick={()=>{pausePhotoTour();movePhoto(1);}}>›</button>
              </>
            )}
          </div>
        </div>
      )}
      {showAiStaging && (
        <div data-assistant-surface="staging" className="ai-staging-modal" onClick={() => setShowAiStaging(false)}>
          <div className="ai-staging-content" onClick={(e) => e.stopPropagation()}>
            <h3>AI Room Staging</h3>
            <p>Choose a staging option, or say Bedroom, Office, Landscape, or Room:</p>
            <div className="staging-buttons">
              <button className="staging-type-button" onClick={async () => { const result = await handleAiStaging('bedroom'); onAssistantResult?.(result); }} disabled={stagingLoading}>🛏️ Bedroom</button>
              <button className="staging-type-button" onClick={async () => { const result = await handleAiStaging('office'); onAssistantResult?.(result); }} disabled={stagingLoading}>💼 Office</button>
              <button className="staging-type-button" onClick={async () => { const result = await handleAiStaging('landscape'); onAssistantResult?.(result); }} disabled={stagingLoading}>🌳 Landscape</button>
              <button className="staging-type-button" onClick={async () => { const result = await handleAiStaging('room'); onAssistantResult?.(result); }} disabled={stagingLoading}>🏠 Room</button>
            </div>
            {stagingError && <p role="alert">{stagingError}</p>}
            {stagingLoading && (
              <div className="staging-loading">
                <div className="loading-spinner"></div>
                <p>Generating AI staging...</p>
              </div>
            )}
            <button className="close-staging-modal" onClick={() => setShowAiStaging(false)}>Cancel</button>
          </div>
        </div>
      )}
      {showLocationMap && hasMapLocation && (
        <div data-assistant-surface="map" className="property-location-map-overlay assistant-location-map-overlay" onClick={() => setShowLocationMap(false)}>
          <div ref={locationDialogRef} tabIndex={-1} className="property-location-map-dialog" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="Interactive property map">
            <button type="button" className="property-location-map-close" onClick={() => setShowLocationMap(false)} aria-label="Close map">×</button>
            <div className="assistant-map-instructions"><strong>Property map</strong><span>Say “zoom in”, “zoom out”, or “go back”.</span><button type="button" onClick={()=>setShowLocationMap(false)}>Back to property details</button></div><div ref={locationMapRef} className="property-location-map-canvas" />
          </div>
        </div>
      )}
      {showCopySuccess && (
        <div className="property-toast property-toast-success" role="status">
          Link copied to clipboard!
        </div>
      )}
    </>
  );
};

export default PropertyDetailModal;
